// The advisor rail: three cards, one per persona, each holding the play that advisor is proposing right
// now (spec §6). Self-contained — it attaches its own state when it mounts and detaches when it leaves —
// so the live stage and the planning stage each render `<Advisors context="live" />` or
// `<Advisors context="planning" />` and nothing else.
//
// In the live stage "execute play" issues the bundle to the simulation under one play id. Before H-hour
// there is no simulation to issue to, so the same button adopts the orders into the plan: through the
// `onAdoptPlan` callback when the planning stage passes one, otherwise straight into the store's plan.

import { useEffect, useMemo } from 'react';
import { relations } from '../../ai/advisors';
import { ADVISOR_PERSONAS } from '../../ai/prompts';
import type { ValidOrder } from '../../ai/validate';
import { useAdvisors } from '../../state/advisors';
import { planUnits, useStore } from '../../state/store';
import { card } from '../../sim/deck';
import { TICKS_PER_MIN } from '../../sim/types';
import type { Graphic, Target, Unit } from '../../sim/types';
import type { Grid } from '../../sim/grid';
import { AdvisorCard, type Citation, type PlayRow } from '../cards/AdvisorCard';
import { transmitEstimateSec } from '../deck/delay';
import { pingCell } from '../ping';
import { gridRef } from '../reports';

export function Advisors({
  context, onAdoptPlan,
}: {
  context: 'planning' | 'live';
  /** Planning stage only: takes the orders the player adopted from a play into its own plan. */
  onAdoptPlan?: (orders: ValidOrder[]) => void;
}) {
  useEffect(() => useAdvisors.getState().attach(context), [context]);

  const plays = useAdvisors((s) => s.plays);
  const refreshing = useAdvisors((s) => s.refreshing);
  const streaming = useAdvisors((s) => s.streaming);
  const expanded = useAdvisors((s) => s.expanded);
  const edits = useAdvisors((s) => s.edits);
  const retargeting = useAdvisors((s) => s.retargeting);

  const snapshot = useStore((s) => s.snapshot);
  const scenario = useStore((s) => s.scenario);
  const grid = useStore((s) => s.grid);
  const dials = useStore((s) => s.dials);
  const events = useStore((s) => s.events);
  const live = useStore((s) => s.sim !== null) && context === 'live';

  // Before the worker exists the task organization stands in, with the ids the simulation will use.
  const units: Unit[] = useMemo(
    () => (snapshot ? snapshot.units.filter((u) => u.side === 0) : scenario ? planUnits(scenario) : []),
    [snapshot, scenario],
  );
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);
  const graphics = scenario?.graphics ?? [];
  const hqAlive = useMemo(() => units.some((u) => u.type === 'hq' && u.alive), [units]);
  const eventCell = useMemo(() => {
    const m = new Map<number, number | undefined>();
    for (const e of events) m.set(e.id, e.cell);
    return m;
  }, [events]);

  // How the three plays stand to each other right now: who contradicted whom, and who converged.
  const relation = useMemo(() => relations(plays), [plays]);

  const tick = snapshot?.tick ?? 0;
  const actLabel = live ? 'execute play' : 'adopt into plan';
  const actTitle = live
    ? 'Issue every card in this play to the command net now'
    : 'Add these cards to the plan; nothing is issued until H-hour';

  return (
    <section
      data-testid="advisors"
      // flex: 1 1 auto (min-height: 0 lets it shrink below its content size) so this is the one
      // flexible member of the right rail; it scrolls its own overflow rather than pushing the
      // order deck below it out of the rail (spec §8 tone; the deck must never clip).
      className="wt-panel thin-scroll flex min-h-0 flex-auto flex-col gap-1 overflow-y-auto p-1"
    >
      <div className="mono sticky -top-1 z-10 -mx-1 -mt-1 mb-0 bg-slate-900 px-2 py-1 text-[9px] uppercase tracking-[0.25em] text-fog">
        Advisors — proposals, not orders
      </div>

      {ADVISOR_PERSONAS.map((persona) => {
        const state = plays[persona.id];
        const orders = edits[persona.id] ?? state?.validated.orders ?? [];
        const rows: PlayRow[] = orders.map((o) => ({
          order: o,
          callsign: unitById.get(o.unitId)?.callsign ?? `UNIT ${o.unitId}`,
          targetSummary: targetSummary(o.target, grid, graphics, unitById),
        }));
        const citations: Citation[] = (state?.play.cited_event_ids ?? []).map((id) => ({ id, cell: eventCell.get(id) }));
        const cost = costOf(orders, unitById, hqAlive, dials);
        const retargetIdx = retargeting && retargeting.persona === persona.id
          ? orders.indexOf(retargeting.order)
          : -1;

        return (
          <AdvisorCard
            key={persona.id}
            persona={persona}
            state={state}
            refreshing={refreshing[persona.id]}
            streaming={streaming[persona.id]}
            relation={relation[persona.id]}
            expanded={expanded === persona.id}
            ageMin={state ? Math.max(0, (tick - state.ageTick) / TICKS_PER_MIN) : 0}
            ageTick={state?.ageTick}
            cost={cost}
            citations={citations}
            rows={rows}
            actLabel={actLabel}
            actTitle={actTitle}
            retargetIdx={retargetIdx >= 0 ? retargetIdx : null}
            retargetReason={live ? undefined : 'Retargeting needs the live map; drop the card and redraw the plan instead'}
            onExecute={() => useAdvisors.getState().executePlay(persona.id, live ? undefined : onAdoptPlan)}
            onExpand={() => useAdvisors.getState().expand(persona.id)}
            onRemove={(idx) => useAdvisors.getState().removeOrder(persona.id, idx)}
            onRetarget={(idx) => useAdvisors.getState().retarget(persona.id, idx)}
            onExecuteEdited={() => useAdvisors.getState().executeEdited(persona.id, live ? undefined : onAdoptPlan)}
            onPing={pingCell}
          />
        );
      })}
    </section>
  );
}

/** What an order is aimed at, phrased the way the rest of the rail phrases things. */
function targetSummary(t: Target, grid: Grid | null, graphics: Graphic[], unitById: Map<number, Unit>): string {
  switch (t.kind) {
    case 'point': return `grid ${gridRef(t.cell, grid)}`;
    case 'graphic': return graphics.find((g) => g.id === t.graphicId)?.name ?? 'a control graphic';
    case 'unit': return unitById.get(t.unitId ?? -1)?.callsign ?? `unit ${t.unitId}`;
    case 'contact': return 'a reported contact';
    case 'text': return 'free text';
    case 'none': return 'no target';
  }
}

/**
 * What the whole bundle costs: every card's issue time adds up on the one command net, while the
 * transmissions overlap, so the bundle is only as slow as its slowest one (src/ui/deck/delay.ts).
 */
function costOf(
  orders: ValidOrder[], unitById: Map<number, Unit>, hqAlive: boolean, dials: Parameters<typeof transmitEstimateSec>[0],
): { issueSec: number; txSec: number } {
  let issueSec = 0;
  let txSec = 0;
  for (const o of orders) {
    issueSec += card(o.card).issueSec;
    txSec = Math.max(txSec, transmitEstimateSec(dials, unitById.get(o.unitId) ?? null, hqAlive));
  }
  return { issueSec, txSec };
}
